import * as React from "react";
import { type IColumn, Stack } from "@fluentui/react";
import { omit } from "ramda";
import { action, autorun, computed, makeObservable, observable, runInAction } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type {
    FleetAuthenticatePeriodSortKey,
    ColumnVisibility,
    FleetAuthenticatePeriod,
} from "../../api";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { SearchState } from "../../utils/search-state";
import { ListSearchField } from "../atoms/list-search-field";
import { DatePicker } from "../organisms/date-picker";
import { endOfISOWeek, startOfISOWeek } from "date-fns";
import { ListLoginReportItemCell } from "../atoms/list-login-report-item-cell";
import {
    type FleetAuthenticatePeriodApiResource,
    type FleetAuthenticatePeriodQuery,
} from "../../domain/repositories/repository-fleet-authenticate-periods";
import { ExportButton, type ExportQuery } from "./export-button";
import { defaultPageSize } from "../../utils/constants";

export interface ListLoginReportProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

import { store } from "../../store";

@observer
export class ListLoginReport extends React.Component<ListLoginReportProps> {
    private _listStateId = createUuid();

    @observable private accessor periods: FleetAuthenticatePeriod[] | undefined;
    @observable private accessor dateStart: Date | undefined = startOfISOWeek(new Date());
    @observable private accessor dateEnd: Date | undefined = endOfISOWeek(new Date());

    constructor(props: ListLoginReportProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.fleetAuthenticatePeriod.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<FleetAuthenticatePeriodSortKey> {
        return store.serviceListStates.fleetAuthenticatePeriod.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<FleetAuthenticatePeriodApiResource> {
        return store.serviceListStates.fleetAuthenticatePeriod.getPaginationState(this.listStateId);
    }

    @computed private get searchState(): SearchState {
        return store.serviceListStates.fleetAuthenticatePeriod.getSearchState(this.listStateId);
    }

    @computed private get columnVisibility(): ColumnVisibility | undefined {
        return store.repositoryColumnVisibility.byQuery()[0];
    }

    @computed private get query(): FleetAuthenticatePeriodQuery {
        // We need to set the page number here which was set by the mobx repository originally.
        const page = this.paginationState.pagination.offset / defaultPageSize;
        const query = {
            pageSize: defaultPageSize,
            ...this.sortState.query,
            ...this.searchState.query,
            page,
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
        } as FleetAuthenticatePeriodQuery;

        return query;
    }

    @computed private get exportQuery(): ExportQuery {
        const query = Object.entries(this.query);
        return { query, basePath: "/api/v1/fleet_authenticate_events/export?" };
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [];

        if (this.columnVisibility?.fleetAuthenticatePeriodList.eventId) {
            columns.push({
                name: store.i18n.t("listLoginReport.column.eventId.name"),
                key: "eventId",
                minWidth: 90,
                maxWidth: 150,
            });
        }
        if (this.columnVisibility?.fleetAuthenticatePeriodList.userEmployeeId) {
            columns.push({
                name: store.i18n.t("listLoginReport.column.user.employeeId.name"),
                key: "userEmployeeId",
                minWidth: 100,
                maxWidth: 150,
            });
        }
        if (this.columnVisibility?.fleetAuthenticatePeriodList.vehicleSerialNumber) {
            columns.push({
                name: store.i18n.t("listLoginReport.column.vehicle.serialNumber.name"),
                key: "vehicleSerialNumber",
                minWidth: 90,
                maxWidth: 150,
            });
        }
        columns.push({
            name: store.i18n.t("listLoginReport.column.loggedInTime.name"),
            key: "loggedInTime",
            minWidth: 100,
            maxWidth: 150,
        });
        columns.push({
            name: store.i18n.t("listLoginReport.column.loggedOutTime.name"),
            key: "loggedOutTime",
            minWidth: 100,
            maxWidth: 150,
        });
        columns.push({
            name: store.i18n.t("listLoginReport.column.loginDuration.name"),
            key: "loginDuration",
            minWidth: 100,
            maxWidth: 150,
        });
        columns.push({
            name: store.i18n.t("listLoginReport.column.logoutType.name"),
            key: "logoutType",
            minWidth: 100,
            maxWidth: 150,
        });

        return columns;
    }

    private renderItem(item: FleetAuthenticatePeriod, column: IColumn): JSX.Element {
        return <ListLoginReportItemCell period={item} column={column} />;
    }

    @action.bound public onDateRangeChanged(dateStart?: Date, dateEnd?: Date): void {
        this.dateStart = dateStart;
        this.dateEnd = dateEnd;
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.fleetAuthenticatePeriod.isInitialized(this.listStateId)) {
            return <></>;
        }
        if (this.periods === undefined) {
            this.periods = [];
            autorun(() => {
                store.fleetAuthenticateEventsApi
                    .fleetAuthenticateEventsListFleetAuthenticatePeriods({ ...this.query })
                    .then((result) => {
                        runInAction(() => {
                            this.periods = result;
                        });
                    });
            });
        }

        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList
                paginationState={this.paginationState}
                commandBar={
                    <Stack horizontal horizontalAlign="space-between">
                        {this.props.commandBar}
                        <ExportButton query={this.exportQuery} />
                    </Stack>
                }
                searchField={
                    <ListSearchField
                        placeholder={store.i18n.t("listLoginReport.search.placeholder")}
                        listStateId={this.listStateId}
                        listState={store.serviceListStates.fleetAuthenticatePeriod}
                    />
                }
                filter={
                    <Stack
                        horizontal
                        style={{ width: "100%" }}
                        horizontalAlign="end"
                        verticalAlign="center"
                        tokens={{ childrenGap: 20 }}
                    >
                        <DatePicker onDateRangeChanged={this.onDateRangeChanged} />
                    </Stack>
                }
            >
                <ElofleetList
                    {...props}
                    onRenderItemColumn={(item: FleetAuthenticatePeriod, _index, column) =>
                        this.renderItem(item, column!)
                    }
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.periods}
                />
            </LayoutList>
        );
    }
}
